import React, { createContext, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { useAtomValue } from "jotai";
import { normalizeRootCauseState } from "../../common/FiltersFormEngine/factories/rootCauseFactory";
import { advancedFormToQuery } from "../../common/FiltersFormEngine/mappers/advancedFormToQuery";
import { advancedQueryToFormState } from "../../common/FiltersFormEngine/mappers/advancedQueryToFormState";
import { buildAppliedFilterPayload } from "../../common/FiltersFormEngine/mappers/filtersToPayload";
import { payloadToDashboardFilters } from "../../common/FiltersFormEngine/mappers/payloadToDashboardFilters";
import { simpleToAdvancedFormState } from "../../common/FiltersFormEngine/mappers/simpleToAdvancedFormState";
import { QueryAction, queryReducer } from "../../common/FiltersFormEngine/queryReducer";
import { isAdvancedFiltersValid, isSimpleFiltersValid } from "../../common/FiltersFormEngine/querySelectors";
import { FilterItem } from "../../common/FiltersFormEngine/types";
import {
	DataQueryPayload,
	FilterCombinationPayload,
	FiltersListDTO,
	UserCustomFiltersListDTO,
} from "../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../datasources/networking/apiRoutes";
import { usePeriodFilter } from "../../hooks/usePreferences";
import { useReadResource } from "../../hooks/useResource";
import { userPeriodFilter, UserPeriodFilter } from "../../state/company";
import {
	addCustomFilterToRegistry,
	deleteFilterFromRegistry,
	renameFilterInRegistry,
	updateFilterInRegistry,
} from "./actions/filterActions";
import {
	DashboardFilterInstance,
	FilterDefinition,
	FilterQuery,
	FiltersRegistry,
	RootCausePerimeterItem,
	RootCauseState,
} from "./types";
import { buildRegistry } from "./utils/index";
import {
	loadAppliedFiltersFromStorage,
	loadDashboardFromStorage,
	loadFromStorage,
	loadRootCauseFromStorage,
	saveAppliedFiltersToStorage,
	saveDashboardToStorage,
	saveRootCauseToStorage,
	saveToStorage,
} from "./utils/persistence";

// Context types
type FiltersContextValue = {
	registry: FiltersRegistry | null;

	updateFavouriteFilter: (filterId: string, updater: (filter: FilterDefinition) => FilterDefinition) => void;
	renameCustomFilter: (filterId: string, name: string) => void;
	removeCustomFilter: (filterId: string) => void;

	applyAdvancedConfiguration: (query: FilterQuery) => void;
	applySavedFilter: (filterId: string) => void;
	advancedMode: boolean;
	enableAdvancedMode: () => void;
	disableAdvancedMode: () => void;

	dashboardFilters: DashboardFilterInstance[];
	addDashboardFilter: (filterId: string) => void;
	clearDashboardFilters: () => void;
	updateDashboardFilter: (instanceId: string, patch: Partial<DashboardFilterInstance>) => void;
	removeDashboardFilter: (instanceId: string) => void;
	toggleDashboardFilterOpen: (instanceId: string) => void;

	addCustomFilter: (filter: FilterDefinition) => void;
	advancedForm: FilterItem[];
	dispatchAdvancedForm: React.Dispatch<QueryAction>;
	initializeAdvancedForm: () => void;

	isHydrating: boolean;
	loadingFilters: boolean;
	isReady: boolean;
	appliedFilters: AppliedFiltersState;

	rootCauseState: RootCauseState;
	addRootCausePerimeterFilter: (filter: RootCausePerimeterItem) => void;
	removeRootCausePerimeterFilter: (filterId: string) => void;
	addRootCauseDashboardFilter: (workspace: "baseline" | "comparison", filterId: string) => void;
	updateRootCauseDashboardFilter: (
		workspace: "baseline" | "comparison",
		instanceId: string,
		patch: Partial<DashboardFilterInstance>
	) => void;
	removeRootCauseDashboardFilter: (workspace: "baseline" | "comparison", instanceId: string) => void;

	updateRootCausePeriod: (workspace: "baseline" | "comparison", filter: UserPeriodFilter) => void;
	rootCauseAdvancedModeModal: {
		open: boolean;
		workspace: "baseline" | "comparison" | null;
		filter: FilterDefinition | null;
	};
	addRootCauseSavedFilter: (workspace: "baseline" | "comparison", filter: FilterDefinition) => void;
	applyRootCauseConfiguration: (workspace: "baseline" | "comparison", query: FilterQuery) => void;
	openRootCauseAdvancedModeModal: (workspace: "baseline" | "comparison", filter: FilterDefinition) => void;
	closeRootCauseAdvancedModeModal: VoidFunction;
	confirmRootCauseAdvancedMode: VoidFunction;
	copyRootCauseWorkspace: (from: "baseline" | "comparison", to: "baseline" | "comparison") => void;

	initializeRootCauseAdvancedForm: (workspace: "baseline" | "comparison") => void;
	openRootCauseSwitchAdvancedModeModal: (workspace: "baseline" | "comparison") => void;
	dispatchRootCauseAdvancedForm: (workspace: "baseline" | "comparison", action: QueryAction) => void;

	isRootCauseAnalysisEnabled: boolean;
	prepareRootCausePayload: (perspective?: "operational" | "logical") => boolean;

	clearRootCausePerimeter: VoidFunction;
	clearRootCauseWorkspace: (workspace: "baseline" | "comparison") => void;
	clearRootCause: VoidFunction;

	applyFilters: (payload: FilterCombinationPayload) => void;
	isApplyEnabled: boolean;
};

// Context
const FiltersContext = createContext<FiltersContextValue | null>(null);

// Context hook
export const useFiltersRegistry = () => {
	const ctx = useContext(FiltersContext);

	if (!ctx) {
		throw new Error("FiltersProvider missing");
	}

	return ctx;
};

// Provider types
type FiltersProviderProps = {
	children: React.ReactNode;
};

// Applied filters types
export interface FiltersDashboardPayload extends FilterCombinationPayload {
	from: string;
	to: string;
}

export type AppliedFiltersState = {
	payload: FiltersDashboardPayload;
	lastAppliedAt: number | null;
};

// Default applied filters payload
const createDefaultPayload = (from: string, to: string): FiltersDashboardPayload => ({
	operator: "and",
	filters: [],
	from,
	to,
});

// Creates a safe copy of the selected period.
const cloneUserPeriodFilter = (period: UserPeriodFilter): UserPeriodFilter => ({
	...period,
	start: period.start.clone(),
	end: period.end.clone(),
	fiscalYears: period.fiscalYears.map((fiscalYear) => ({
		...fiscalYear,
		start: fiscalYear.start.clone(),
		end: fiscalYear.end.clone(),
	})),
});

// Creates the comparison period one year before the baseline.
const createPreviousYearPeriod = (period: UserPeriodFilter): UserPeriodFilter => {
	const start = period.start.subtract(1, "year");
	const end = period.end.subtract(1, "year");

	const matchingFiscalYear = period.fiscalYears.find(
		(fiscalYear) => fiscalYear.start.isSame(start, "day") && fiscalYear.end.isSame(end, "day")
	);

	return {
		...cloneUserPeriodFilter(period),
		selectedPeriodId: matchingFiscalYear?.id ?? period.selectedPeriodId,
		start,
		end,
	};
};

// Provider
export const FiltersProvider = ({ children }: FiltersProviderProps) => {
	const globalPeriodFilter = useAtomValue(userPeriodFilter);

	// Period preferences
	const { formattedFrom: from, formattedTo: to } = usePeriodFilter();

	// Root cause state
	const [rootCauseState, setRootCauseState] = useState<RootCauseState>(() => {
		return normalizeRootCauseState(loadRootCauseFromStorage());
	});

	// Sync Root Cause periods with the global period.
	useEffect(() => {
		if (!globalPeriodFilter) {
			return;
		}

		const baseline = cloneUserPeriodFilter(globalPeriodFilter);
		const comparison = createPreviousYearPeriod(globalPeriodFilter);

		setRootCauseState((prev) => ({
			...prev,
			periods: {
				baseline,
				comparison,
			},
		}));
	}, [globalPeriodFilter]);

	// Root cause advanced mode modal state
	const [rootCauseAdvancedModeModal, setRootCauseAdvancedModeModal] = useState<{
		open: boolean;
		workspace: "baseline" | "comparison" | null;
		filter: FilterDefinition | null;
	}>({
		open: false,
		workspace: null,
		filter: null,
	});

	// Applied filters state
	const [appliedFilters, setAppliedFilters] = useState<AppliedFiltersState>(() => {
		const stored = loadAppliedFiltersFromStorage();

		return (
			stored ?? {
				payload: createDefaultPayload(from, to),
				lastAppliedAt: null,
			}
		);
	});

	// Registry cache
	const [registry, setRegistry] = useState<FiltersRegistry | null>(() => {
		return loadFromStorage();
	});

	// Dashboard state
	const [dashboardFilters, setDashboardFilters] = useState<DashboardFilterInstance[]>(() => {
		const stored = loadDashboardFromStorage();

		return stored?.filters ?? [];
	});

	// Advanced form state
	const initialAdvancedForm: FilterItem[] = [];

	const [advancedFormInitialized, setAdvancedFormInitialized] = useState(false);

	const [advancedForm, dispatchAdvancedForm] = useReducer(queryReducer, initialAdvancedForm);

	// Advanced mode state
	const [advancedMode, setAdvancedMode] = useState(() => {
		const stored = loadDashboardFromStorage();

		return stored?.advancedMode ?? false;
	});

	// API resources
	const { data: availableFilters, isLoading: loadingAvailable } = useReadResource<undefined, FiltersListDTO>({
		route: ApiRoutes.FiltersAvailable,
	});

	const { data: userSavedFilters, isLoading: loadingSaved } = useReadResource<undefined, UserCustomFiltersListDTO>({
		route: ApiRoutes.CustomFilter,
	});

	// Registry computed from API data
	const computedRegistry = useMemo(() => {
		return buildRegistry({
			available: availableFilters,
			saved: userSavedFilters,
		});
	}, [availableFilters, userSavedFilters]);

	// Current dashboard payload snapshot
	const currentPayload = useMemo(() => {
		return buildAppliedFilterPayload({
			mode: advancedMode ? "advanced" : "simple",
			dashboardFilters,
			advancedForm,
		});
	}, [advancedMode, dashboardFilters, advancedForm]);

	// Dirty check
	const hasChanges = useMemo(() => {
		return JSON.stringify(currentPayload) !== JSON.stringify(appliedFilters.payload);
	}, [currentPayload, appliedFilters]);

	// Dashboard validation
	const canApply = isSimpleFiltersValid(dashboardFilters) || isAdvancedFiltersValid(advancedForm);

	const isApplyEnabled = canApply && hasChanges;

	// Root cause workspace validation
	// const isRootCauseWorkspaceValid = (workspace: "baseline" | "comparison") => {
	// 	const current = rootCauseState[workspace];

	// 	return current.advancedMode
	// 		? isAdvancedFiltersValid(current.advancedForm)
	// 		: isSimpleFiltersValid(current.dashboardFilters);
	// };

	// Root cause analysis validation
	// const isRootCauseAnalysisEnabled = useMemo(() => {
	// 	return isRootCauseWorkspaceValid("baseline") && isRootCauseWorkspaceValid("comparison");
	// }, [rootCauseState]);

	const isRootCauseWorkspaceValid = (workspace: "baseline" | "comparison") => {
		const period = rootCauseState.periods[workspace];

		return !!period?.start && !!period?.end;
	};

	const isRootCauseAnalysisEnabled = useMemo(() => {
		return isRootCauseWorkspaceValid("baseline") && isRootCauseWorkspaceValid("comparison");
	}, [rootCauseState.periods]);

	// Loading state
	const isHydrating = !registry || (advancedMode && !advancedFormInitialized);

	const loadingFilters = loadingAvailable || loadingSaved;

	const isReady = !!registry && !loadingFilters;

	// Sync applied period when global period changes
	useEffect(() => {
		setAppliedFilters((prev) => {
			if (!prev?.payload) {
				return {
					payload: createDefaultPayload(from, to),
					lastAppliedAt: null,
				};
			}

			return {
				...prev,
				payload: {
					...prev.payload,
					from,
					to,
				},
			};
		});
	}, [from, to]);

	// Sync registry cache
	useEffect(() => {
		if (!computedRegistry) {
			return;
		}

		setRegistry(computedRegistry);
	}, [computedRegistry]);

	// Persist registry
	useEffect(() => {
		if (!registry) {
			return;
		}

		saveToStorage(registry);
	}, [registry]);

	// Persist dashboard state
	useEffect(() => {
		saveDashboardToStorage({
			filters: dashboardFilters,
			advancedMode,
		});
	}, [dashboardFilters, advancedMode, appliedFilters]);

	// Persist root cause state
	useEffect(() => {
		saveRootCauseToStorage(rootCauseState);
	}, [rootCauseState]);

	// Initialize advanced form from dashboard state
	useEffect(() => {
		if (!advancedMode) {
			return;
		}

		if (advancedFormInitialized) {
			return;
		}

		const advancedFilter = dashboardFilters.find((filter) => filter.mode === "advanced");

		if (!advancedFilter?.query) {
			return;
		}

		dispatchAdvancedForm({
			type: "RESET",
			payload: advancedQueryToFormState(advancedFilter.query),
		});

		setAdvancedFormInitialized(true);
	}, [advancedMode, dashboardFilters, advancedFormInitialized]);

	// Registry actions
	const updateFavouriteFilter = (filterId: string, updater: (filter: FilterDefinition) => FilterDefinition) => {
		setRegistry((prev) => {
			if (!prev) {
				return prev;
			}

			return updateFilterInRegistry(prev, filterId, updater);
		});
	};

	const renameCustomFilter = (filterId: string, name: string) => {
		setRegistry((prev) => {
			if (!prev) {
				return prev;
			}

			return renameFilterInRegistry(prev, filterId, name);
		});
	};

	const removeCustomFilter = (filterId: string) => {
		setRegistry((prev) => {
			if (!prev) {
				return prev;
			}

			return deleteFilterFromRegistry(prev, filterId);
		});
	};

	const addCustomFilter = (filter: FilterDefinition) => {
		setRegistry((prev) => {
			if (!prev) {
				return prev;
			}

			return addCustomFilterToRegistry(prev, filter);
		});
	};

	// Dashboard actions
	const addDashboardFilter = (filterId: string) => {
		const filter = registry?.byId[filterId];

		if (!filter) {
			return;
		}

		const defaultOperator = filter.field?.operators?.[0]?.operator;

		setDashboardFilters((prev) => [
			...prev,
			{
				id: crypto.randomUUID(),
				filterId,
				operator: defaultOperator,
				value: undefined,
				mode: "simple",
				open: false,
				enabled: true,
				createdAt: new Date().toISOString(),
			},
		]);
	};

	const updateDashboardFilter = (instanceId: string, patch: Partial<DashboardFilterInstance>) => {
		setDashboardFilters((prev) =>
			prev.map((filter) => (filter.id === instanceId ? { ...filter, ...patch } : filter))
		);
	};

	const removeDashboardFilter = (instanceId: string) => {
		if (dashboardFilters.length === 1) return clearDashboardFilters();
		setDashboardFilters((prev) => prev.filter((filter) => filter.id !== instanceId));
	};

	const toggleDashboardFilterOpen = (instanceId: string) => {
		setDashboardFilters((prev) =>
			prev.map((filter) => (filter.id === instanceId ? { ...filter, open: !filter.open } : filter))
		);
	};

	const clearDashboardFilters = () => {
		setDashboardFilters([]);

		setAdvancedFormInitialized(false);

		setAdvancedMode(false);

		dispatchAdvancedForm({
			type: "RESET",
			payload: [],
		});

		const resetApplied: AppliedFiltersState = {
			payload: {
				filters: [],
				from,
				to,
			},
			lastAppliedAt: null,
		};

		setAppliedFilters(resetApplied);

		saveAppliedFiltersToStorage(resetApplied);
	};

	// Advanced mode actions
	const enableAdvancedMode = () => {
		setAdvancedMode(true);
	};

	const disableAdvancedMode = () => {
		setAdvancedMode(false);
	};

	const applyAdvancedConfiguration = (query: FilterQuery) => {
		setAdvancedFormInitialized(true);

		enableAdvancedMode();

		dispatchAdvancedForm({
			type: "RESET",
			payload: advancedQueryToFormState(query),
		});
	};

	// Convert simple filters into advanced form
	const initializeAdvancedForm = () => {
		const advancedFilter = dashboardFilters.find((filter) => filter.mode === "advanced");

		const initialState = advancedFilter?.query
			? advancedQueryToFormState(advancedFilter.query)
			: simpleToAdvancedFormState(dashboardFilters).filters;

		dispatchAdvancedForm({
			type: "RESET",
			payload: initialState,
		});

		setAdvancedFormInitialized(true);
	};

	// Sync advanced query into dashboard filters
	const syncAdvancedQuery = (query: FilterQuery) => {
		setDashboardFilters((prev) => {
			const withoutAdvanced = prev.filter((f) => f.mode !== "advanced");

			return [
				...withoutAdvanced,
				{
					id: "advanced-root",
					filterId: "advanced",
					mode: "advanced",
					query,
					enabled: true,
					createdAt: new Date().toISOString(),
				},
			];
		});
	};

	// Auto sync advanced form
	useEffect(() => {
		if (!advancedMode) {
			return;
		}

		if (!advancedForm.length) {
			return;
		}

		syncAdvancedQuery(advancedFormToQuery(advancedForm));
	}, [advancedForm, advancedMode]);

	// Apply saved filter
	const applySavedFilter = (filterId: string) => {
		const filter = registry?.byId[filterId];

		if (!filter?.filter) {
			return;
		}

		// Advanced configuration
		if (filter.filterType === "configuration") {
			clearDashboardFilters();
			applyAdvancedConfiguration(filter.filter);
			return;
		}

		// Simple filters
		disableAdvancedMode();

		const dashboardState = payloadToDashboardFilters({
			query: filter.filter,
		});

		setDashboardFilters((prev) => [...prev, ...dashboardState]);
	};

	// Apply filters
	const applyFilters = (payload: FilterCombinationPayload) => {
		const next: AppliedFiltersState = {
			payload: {
				...payload,
				from,
				to,
			},
			lastAppliedAt: Date.now(),
		};

		setAppliedFilters(next);
		saveAppliedFiltersToStorage(next);
	};

	// Root cause period actions
	const updateRootCausePeriod = (workspace: "baseline" | "comparison", filter: UserPeriodFilter) => {
		setRootCauseState((prev) => ({
			...prev,
			periods: {
				...prev.periods,
				[workspace]: filter,
			},
		}));
	};

	// Root cause modal actions
	const openRootCauseAdvancedModeModal = (workspace: "baseline" | "comparison", filter: FilterDefinition) => {
		setRootCauseAdvancedModeModal({
			open: true,
			workspace,
			filter,
		});
	};

	const openRootCauseSwitchAdvancedModeModal = (workspace: "baseline" | "comparison") => {
		setRootCauseAdvancedModeModal({
			open: true,
			workspace,
			filter: null,
		});
	};

	const closeRootCauseAdvancedModeModal = () => {
		setRootCauseAdvancedModeModal({
			open: false,
			workspace: null,
			filter: null,
		});
	};

	const confirmRootCauseAdvancedMode = () => {
		const { workspace, filter } = rootCauseAdvancedModeModal;

		if (!workspace) {
			return;
		}

		if (filter) {
			applyRootCauseConfiguration(workspace, filter.filter);
			closeRootCauseAdvancedModeModal();
			return;
		}

		initializeRootCauseAdvancedForm(workspace);
		closeRootCauseAdvancedModeModal();
	};

	// Root cause perimeter actions
	const addRootCausePerimeterFilter = (filter: RootCausePerimeterItem) => {
		setRootCauseState((prev) => {
			const exists = prev.perimeter.some((f) => f.id === filter.id);

			if (exists) {
				return prev;
			}

			return {
				...prev,
				perimeter: [...prev.perimeter, filter],
			};
		});
	};

	const removeRootCausePerimeterFilter = (filterId: string) => {
		setRootCauseState((prev) => ({
			...prev,
			perimeter: prev.perimeter.filter((f) => f.id !== filterId),
		}));
	};

	const clearRootCausePerimeter = () => {
		setRootCauseState((prev) => ({
			...prev,
			perimeter: [],
		}));
	};

	// Root cause dashboard actions
	const addRootCauseDashboardFilter = (workspace: "baseline" | "comparison", filterId: string) => {
		const filter = registry?.byId[filterId];
		if (!filter) return;

		const defaultOperator = filter.field?.operators?.[0]?.operator;

		const instance: DashboardFilterInstance = {
			id: crypto.randomUUID(),
			filterId,
			operator: defaultOperator,
			value: undefined,
			mode: "simple",
			open: false,
			enabled: true,
			createdAt: new Date().toISOString(),
		};

		setRootCauseState((prev) => ({
			...prev,
			[workspace]: {
				...prev[workspace],
				dashboardFilters: [...prev[workspace].dashboardFilters, instance],
			},
		}));
	};

	// Update Root caouse Dashboard filters
	const updateRootCauseDashboardFilter = (
		workspace: "baseline" | "comparison",
		instanceId: string,
		patch: Partial<DashboardFilterInstance>
	) => {
		setRootCauseState((prev) => ({
			...prev,
			[workspace]: {
				...prev[workspace],
				dashboardFilters: prev[workspace].dashboardFilters.map((filter) =>
					filter.id === instanceId
						? {
								...filter,
								...patch,
							}
						: filter
				),
			},
		}));
	};

	const removeRootCauseDashboardFilter = (workspace: "baseline" | "comparison", instanceId: string) => {
		setRootCauseState((prev) => ({
			...prev,
			[workspace]: {
				...prev[workspace],
				dashboardFilters: prev[workspace].dashboardFilters.filter((filter) => filter.id !== instanceId),
			},
		}));
	};

	const addRootCauseSavedFilter = (workspace: "baseline" | "comparison", filter: FilterDefinition) => {
		const instances = payloadToDashboardFilters({
			query: filter.filter,
		});

		setRootCauseState((prev) => ({
			...prev,

			[workspace]: {
				...prev[workspace],

				dashboardFilters: [...prev[workspace].dashboardFilters, ...instances],
			},
		}));
	};

	const clearRootCauseWorkspace = (workspace: "baseline" | "comparison") => {
		setRootCauseState((prev) => ({
			...prev,
			[workspace]: {
				...prev[workspace],
				dashboardFilters: [],
				advancedMode: false,
				advancedForm: [],
				appliedFilters: {
					payload: createDefaultPayload(from, to),
					lastAppliedAt: null,
				},
			},
		}));
	};

	const clearRootCause = () => {
		setRootCauseAdvancedModeModal({
			open: false,
			workspace: null,
			filter: null,
		});

		setRootCauseState(normalizeRootCauseState(null));
	};

	// Root cause advanced actions
	const applyRootCauseConfiguration = (workspace: "baseline" | "comparison", query: FilterQuery) => {
		setRootCauseState((prev) => ({
			...prev,

			[workspace]: {
				...prev[workspace],
				dashboardFilters: [],
				advancedMode: true,
				advancedForm: advancedQueryToFormState(query),
			},
		}));
	};

	// Used for switch toggle
	const initializeRootCauseAdvancedForm = (workspace: "baseline" | "comparison") => {
		setRootCauseState((prev) => {
			const dashboardFilters = prev[workspace].dashboardFilters;

			return {
				...prev,

				[workspace]: {
					...prev[workspace],
					advancedMode: true,
					advancedForm: simpleToAdvancedFormState(dashboardFilters).filters,
				},
			};
		});
	};

	const dispatchRootCauseAdvancedForm = (workspace: "baseline" | "comparison", action: QueryAction) => {
		setRootCauseState((prev) => ({
			...prev,
			[workspace]: {
				...prev[workspace],
				advancedForm: queryReducer(prev[workspace].advancedForm, action),
			},
		}));
	};

	const copyRootCauseWorkspace = (from: "baseline" | "comparison", to: "baseline" | "comparison") => {
		if (from === to) return;

		setRootCauseState((prev) => ({
			...prev,
			[to]: {
				...prev[to],
				dashboardFilters: prev[from].dashboardFilters.map((filter) => ({
					...filter,
					id: crypto.randomUUID(),
					open: false,
				})),
				advancedMode: prev[from].advancedMode,
				advancedForm: JSON.parse(JSON.stringify(prev[from].advancedForm)),
			},
		}));
	};

	// Root cause payload actions
	const buildRootCauseWorkspacePayload = (workspace: "baseline" | "comparison"): DataQueryPayload => {
		const current = rootCauseState[workspace];

		const query = buildAppliedFilterPayload({
			mode: current.advancedMode ? "advanced" : "simple",
			dashboardFilters: current.dashboardFilters,
			advancedForm: current.advancedForm,
		});

		const period = rootCauseState.periods[workspace];

		return {
			...query,
			from: period.start.format("YYYY-MM-DD"),
			to: period.end.format("YYYY-MM-DD"),
		};
	};

	const prepareRootCausePayload = (perspective?: "operational" | "logical") => {
		if (!isRootCauseAnalysisEnabled) {
			return false;
		}

		const baseline = buildRootCauseWorkspacePayload("baseline");
		const comparison = buildRootCauseWorkspacePayload("comparison");

		setRootCauseState((prev) => ({
			...prev,
			payload: {
				perimeter: prev.perimeter.map((item) => Number(item.id)),
				baseline,
				comparison,
				perspective,
			},
		}));

		return true;
	};

	// Context value
	const value = useMemo(
		() => ({
			registry,

			updateFavouriteFilter,
			renameCustomFilter,
			removeCustomFilter,

			applyAdvancedConfiguration,
			applySavedFilter,

			addCustomFilter,

			advancedMode,
			enableAdvancedMode,
			disableAdvancedMode,

			dashboardFilters,
			addDashboardFilter,
			clearDashboardFilters,
			updateDashboardFilter,
			removeDashboardFilter,
			toggleDashboardFilterOpen,

			advancedForm,
			dispatchAdvancedForm,
			initializeAdvancedForm,

			isHydrating,
			loadingFilters,
			isReady,

			appliedFilters,
			applyFilters,

			rootCauseState,
			updateRootCausePeriod,
			addRootCausePerimeterFilter,
			removeRootCausePerimeterFilter,
			addRootCauseDashboardFilter,
			updateRootCauseDashboardFilter,
			removeRootCauseDashboardFilter,

			addRootCauseSavedFilter,
			confirmRootCauseAdvancedMode,
			applyRootCauseConfiguration,
			openRootCauseAdvancedModeModal,
			rootCauseAdvancedModeModal,
			closeRootCauseAdvancedModeModal,
			copyRootCauseWorkspace,

			initializeRootCauseAdvancedForm,
			openRootCauseSwitchAdvancedModeModal,
			dispatchRootCauseAdvancedForm,

			isRootCauseAnalysisEnabled,
			prepareRootCausePayload,

			clearRootCausePerimeter,
			clearRootCauseWorkspace,
			clearRootCause,

			isApplyEnabled,
		}),
		[
			registry,
			isHydrating,
			loadingFilters,
			isReady,
			dashboardFilters,
			advancedMode,
			appliedFilters,
			rootCauseState,
			rootCauseAdvancedModeModal,
		]
	);

	// Provider
	return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
};
